import { Item, ItemGroupPreview, ItemsResponseDTO } from '@dto/architecture.dto';
import { FormProviderProps } from 'antd/es/form/context';
import { AxiosError } from "axios";
import { useContext, useEffect, useState, type JSX } from 'react';
import { API_URL } from "src/data/Api";
import { Log } from "src/service/log.service";
import { postApi } from "src/util/apiCalls";
import { GLOB } from 'src/util/Glob';
import { makeItemLabelRoot } from '../children/Items';
import { AllItemsContext } from '../comp/AllItems';
import ModalSelectItems from '../comp/ModalSelectItems';
import TagsList from '../comp/TagsList';
import useSelectModal from "../hook/useSelectModal";
import { AdditionalApiPropsContext, RuleContext, ruleHwType } from "../XmRule";
import { EnableItemId, RuleGroup } from "../XmRuleList";
import AddButton from './AddButton';

export interface CustomFormCompProps<Value> {
  value?: Value;
  onChange?: (value: Value) => void;
}

const Items = ({ value, onChange }: CustomFormCompProps<Item[]>) => {
  const { allItems, isLoadingAll } = useContext(AllItemsContext);
  const handleFormFinish: FormProviderProps['onFormFinish'] = (_, { forms }) => {
    const { items }: { items: Item[]; } = forms['selected-items'].getFieldsValue(true);
    onChange?.(items);
  };

  const [showModalSelect, contextHolder] = useSelectModal<Item[]>({
    onFormFinish: handleFormFinish,
  });

  function makeTreeObj(itemTree: Item) {
    let title: JSX.Element;
    let deleted = false;
    allItems.some(item => {
      if (item.item_id === itemTree.item_id) {
        title = makeItemLabelRoot(item);
        deleted = item.deleted ?? false;

        return true;
      }

      return false;
    });

    return { title, key: itemTree.item_id, deleted };
  }

  const handleAdd = () => {
    showModalSelect({
      initialValues: { items: value },
      content: <ModalSelectItemsExclude />
    });
  };

  const handleCloseItem = (id: string) => () => {
    onChange?.(value?.filter(item => item.item_id !== id));
  };

  return <>
    {!isLoadingAll &&
      <TagsList
        items={value?.map(makeTreeObj).sort((a, b) => GLOB.naturalSort(a.title, b.title))}
        onClose={handleCloseItem}
      />
    }
    {contextHolder}
    <AddButton onClick={handleAdd} />
  </>;
};
function ModalSelectItemsExclude() {
  const { cls, subsystem, hw_type, allItems, isLoadingAll } = useContext(AllItemsContext);
  const { linuxExclude, getValue } = useContext(RuleContext);
  const [searchedItems, setSearchedItems] = useState<Item[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { exclude, additionalPayload } = useContext(AdditionalApiPropsContext);

  useEffect(() => {
    const { regex, parents, tags } = getValue<RuleGroup>();
    const controller = new AbortController();
    const params = exclude?.select ? { ...exclude.select } : {};  // additionalApiParm.items.select

    postApi<ItemsResponseDTO, ItemGroupPreview>(
      API_URL.ARCHITECTURE + '/preview',
      {
        class: cls,
        subsystem,
        hw_type: ruleHwType(cls, hw_type),
        regex,
        tags: tags?.map(t => t.tag_id),
        linuxExclude,
        parents: parents && {
          ...parents,
          regex: parents.regex?.filter(parent => !!parent.subsystem),
          item_ids: parents.items?.map(item => item.item_id),
          tags: parents.tags?.map(tag => ({ tag_id: tag.tag_id, subsystem: tag.subsystem }))
        },
        ...additionalPayload
      },
      { signal: controller.signal, params: { rootParent: true, ...params } },
    ).then(
      ({ data: { data } }) => setSearchedItems(data),
      (reason: AxiosError) => Log.error('Failed to retrieve items!', reason)
    ).finally(() => setIsLoading(false));

    return () => controller.abort();
  }, []);

  function makeItemOfList({ item_id, disabled }: EnableItemId) {
    let title: JSX.Element;
    let deleted = false;
    allItems.some(item => {
      if (item.item_id === item_id) {
        title = makeItemLabelRoot(item);
        deleted = item.deleted ?? false;

        return true;
      }

      return false;
    });

    return { title, key: item_id, disabled, deleted };
  }

  return (
    <ModalSelectItems<EnableItemId, { items: EnableItemId[]; }>
      makeItemOfList={makeItemOfList}
      makeLabel={makeItemLabelRoot}
      isLoading={isLoading && isLoadingAll}
      treeItems={searchedItems}
      handleCheckItem={(item) => ({ item_id: item.item_id, disabled: false })}
    />
  );
}

export default Items;
